import type { PropsWithChildren } from 'react'
import { useCallback, useEffect, useState } from 'react'

import type { UserSettingsUiTheme } from '@api'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUnit } from 'effector-react'
import upperFirst from 'lodash/upperFirst'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import * as z from 'zod'

import UploadIcon from '@/assets/icons/icon-upload.svg'
import ThemeDark from '@/assets/images/theme-dark.svg'
import ThemeLight from '@/assets/images/theme-light.svg'
import ThemeSystem from '@/assets/images/theme-system.svg'
import { Page } from '@/components/layout/page/page.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Checkbox } from '@/components/ui/checkbox'
import { TextField } from '@/components/ui/text-field.tsx'
import { useToast } from '@/components/ui/toast'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $account } from '@/models/account.ts'
import { openDialog } from '@/models/dialogs.ts'
import { AgencyLogo } from '@/modules/accounts/ui/AgencyLogo'
import { AgencyLogoSelector } from '@/modules/accounts/ui/AgencyLogo/AgencyLogoSelector'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'
import { emailSchema } from '@/types/schemas.ts'
import { handleError } from '@/utils/error'
import { classNames } from '@/utils/helpers'
import { getImgAsDataUrl } from '@/utils/image'
import { useDebouncedCallback } from '@/utils/useDebouncedCallback'
import { useTheme } from '@/utils/useTheme'

import { useAccount } from '../../accounts/hooks/useAccounts'
import { useUpdateSettings } from '../hooks/useSettings'

const profileSettingsSchema = z.object({
  email: emailSchema,
  name: z.string(),
})

export type ProfileSettings = z.infer<typeof profileSettingsSchema>

export const ProfileSettingsTab = () => {
  const toaster = useToast()
  const account = useUnit($account)
  const { userSettings, updateUserSettings } = useAccount()

  const [selectedLightMode, setSelectedLightMode] = useState<
    string | undefined
  >(account?.customLogoLight)
  const [selectedDarkMode, setSelectedDarkMode] = useState<string | undefined>(
    account?.customLogoDark,
  )

  const handleConvertImgToDataUrl = useCallback(async () => {
    if (account?.customLogoDark && account?.customLogoLight) {
      const agencyLogoDarkDataUrl = await getImgAsDataUrl(
        account?.customLogoDark,
      )
      const agencyLogoLightDataUrl = await getImgAsDataUrl(
        account?.customLogoLight,
      )

      setSelectedLightMode(agencyLogoLightDataUrl)
      setSelectedDarkMode(agencyLogoDarkDataUrl)
    }
  }, [account?.customLogoLight, account?.customLogoDark])

  useEffect(() => {
    handleConvertImgToDataUrl()
  }, [
    account?.customLogoLight,
    account?.customLogoDark,
    handleConvertImgToDataUrl,
  ])

  const [showAgencyLogoSelector, setShowAgencyLogoSelector] = useState(false)
  const {
    updateProfileMutation,
    updateAgencyLogoMutation,
    resetAgencyLogoMutation,
  } = useUpdateSettings()

  const handleChangeHasOnlyfansSameTheme = useCallback(
    (value: boolean) => {
      updateUserSettings({ applyThemeOnOnlyFans: value })
    },
    [updateUserSettings],
  )

  const form = useForm<ProfileSettings>({
    mode: 'onChange',
    resolver: zodResolver(profileSettingsSchema),
    defaultValues: {
      email: account?.email,
      name: account?.name,
    },
  })

  const {
    formState: { errors },
    handleSubmit,
    control,
  } = form

  const onSubmit = useDebouncedCallback(
    1000,
    (values: ProfileSettings) => {
      updateProfileMutation.mutateAsync(values)
    },
    [updateProfileMutation],
  )

  const handleUploadAgencyLogo = useCallback(
    async (props: { ligthMode: Blob | null; darkMode: Blob | null }) => {
      setShowAgencyLogoSelector(false)

      try {
        if (props.ligthMode) {
          await updateAgencyLogoMutation.mutateAsync({
            image: props.ligthMode,
            isDarkMode: false,
          })
        }
        if (props.darkMode) {
          await updateAgencyLogoMutation.mutateAsync({
            image: props.darkMode,
            isDarkMode: true,
          })
        }
        toaster.success('Agency logo updated')
      } catch (error) {
        handleError(error, 'Failed to update agency logo')
      }
    },
    [toaster, updateAgencyLogoMutation],
  )

  const handleResetAgencyLogo = useCallback(async () => {
    try {
      await resetAgencyLogoMutation.mutateAsync()
      toaster.success('Agency logo reset')
    } catch (error) {
      handleError(error, 'Failed to reset agency logo')
    }
  }, [resetAgencyLogoMutation, toaster])

  const handleOpenSelectImageDialog = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.IMAGE_SELECTOR,
    })
  }, [])

  return (
    <Page>
      <div className="flex  flex-col justify-between gap-4 border-b pb-8 md:flex-row">
        <div
          className="flex gap-4"
          data-testid="profile-settings-image"
        >
          <CreatorAvatar
            avatarSrc={account?.profilePicture}
            name={account?.name || ''}
            className="bg-border h-20 w-20 flex-shrink-0 rounded-full"
          />
          <div className="flex flex-col pt-3">
            <h5 className="text-title font-medium">Profile Picture</h5>
            <p className="text-secondary-300 pt-1 text-sm font-medium">
              We support PNGs, JPEGs and GIFs under 10MB
            </p>
          </div>
        </div>
        <div className="pt-4">
          <Button
            onClick={handleOpenSelectImageDialog}
            className="text-title flex h-[42px] gap-2 text-base"
            variant="outline"
            data-testid="profile-settings-image-upload-button"
          >
            <UploadIcon
              className="text-primary-500"
              height={15}
              width={15}
            />
            Select a file
          </Button>
        </div>
      </div>
      <div className="flex flex-col justify-between gap-4 border-b py-8 md:flex-row">
        <div className="flex gap-4">
          <div className="flex flex-col justify-center">
            <AgencyLogo
              useLink={false}
              data-testid="agency-logo"
            />
          </div>
          <div className="flex flex-col">
            <h5 className="text-title font-medium">Agency Logo</h5>
            <p className="text-secondary-300 pt-1 text-sm font-medium">
              This logo will be displayed on your dashboard
            </p>
          </div>
        </div>
        <div className="pt-4">
          <Button
            onClick={() => setShowAgencyLogoSelector(true)}
            className="text-title flex h-[42px] gap-2 text-base"
            variant="outline"
            data-testid="agency-logo-upload-button"
          >
            <UploadIcon
              className="text-primary-500"
              height={15}
              width={15}
            />
            Select a file
          </Button>
          <AgencyLogoSelector
            isOpen={showAgencyLogoSelector}
            onClose={() => setShowAgencyLogoSelector(false)}
            onReset={handleResetAgencyLogo}
            onSelect={handleUploadAgencyLogo}
            selectedDarkMode={selectedDarkMode}
            selectedLightMode={selectedLightMode}
            setSelectedDarkMode={setSelectedDarkMode}
            setSelectedLightMode={setSelectedLightMode}
          />
        </div>
      </div>
      <FormProvider {...form}>
        <form onChange={handleSubmit(onSubmit)}>
          <div className="flex flex-col justify-between gap-4 border-b py-[32px] xl:flex-row">
            <div className="flex flex-col">
              <h5 className="text-title font-medium">Display Name</h5>
              <p className="text-secondary-300 pt-1 text-sm font-medium">
                This name will be displayed on your profile
              </p>
            </div>
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <div className="w-full max-w-[280px]">
                  <TextField
                    {...field}
                    data-testid="account-name-input"
                    className={classNames('h-[48px]', {
                      'border-danger-default': !!errors.name,
                    })}
                  />
                  {errors.name && (
                    <p className="text-danger-default mt-2 text-xs">
                      {errors.name.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
          <div className="flex flex-col justify-between gap-4 border-b py-[32px] xl:flex-row">
            <div className="flex flex-col">
              <h5 className="text-title font-medium">E-Mail Address</h5>
              <p className="text-secondary-300 pt-1 text-sm font-medium">
                The address where we are going to send notifications
              </p>
            </div>
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <div className="w-full max-w-[280px]">
                  <TextField
                    {...field}
                    data-testid="account-email-input"
                    className={classNames('h-[48px]', {
                      'border-danger-default': !!errors.name,
                    })}
                  />
                  {errors.email && (
                    <p className="text-danger-default mt-2 text-xs">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
        </form>
      </FormProvider>
      <div className="flex flex-col justify-between gap-4 py-[32px] xl:flex-row">
        <div className="flex flex-col">
          <h5 className="text-title font-medium">Appearance</h5>
          <p className="text-secondary-300 pt-1 text-sm font-medium">
            Customize how CreatorHero looks
          </p>
        </div>
        <div className="space-y-4">
          <div className="flex gap-4">
            <ThemeOption
              themeName="system"
              testId="profile-settings-theme-system"
            >
              <ThemeSystem />
            </ThemeOption>
            <ThemeOption
              themeName="light"
              testId="profile-settings-theme-light"
            >
              <ThemeLight />
            </ThemeOption>
            <ThemeOption
              themeName="dark"
              testId="profile-settings-theme-dark"
            >
              <ThemeDark />
            </ThemeOption>
          </div>
          <div>
            <label
              htmlFor="isSameThemeForOnlyfans"
              className="flex w-max cursor-pointer items-center gap-2"
            >
              <Checkbox
                id="isSameThemeForOnlyfans"
                checked={userSettings?.applyThemeOnOnlyFans}
                onCheckedChange={handleChangeHasOnlyfansSameTheme}
                className="border"
                data-testid="apply-theme-on-only-fans-checkbox"
              />
              <span className="text-title text-sm">
                Use same theme on OnlyFans
              </span>
            </label>
          </div>
        </div>
      </div>
    </Page>
  )
}

interface Props extends PropsWithChildren {
  themeName: 'light' | 'dark' | 'system'
  testId: string
}

const ThemeOption = ({ themeName, children, testId }: Props) => {
  const { updateUserSettings } = useAccount()
  const { theme, changeTheme } = useTheme()

  const handleChange = useCallback(() => {
    changeTheme(themeName)

    const theme = upperFirst(themeName)! as UserSettingsUiTheme

    updateUserSettings({
      theme,
    })
  }, [changeTheme, themeName, updateUserSettings])

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        onClick={handleChange}
        className={classNames(
          'border-background cursor-pointer rounded-lg border-2',
          theme === themeName && 'border-primary-500',
        )}
        data-testid={testId}
      >
        {children}
      </div>
      <p
        className={classNames(
          'text-title text-sm font-medium',
          theme === themeName && 'text-primary-500',
        )}
      >
        {themeName.charAt(0).toUpperCase() + themeName.slice(1)}
      </p>
    </div>
  )
}
